<template>
  <main-section
    :isEditMode="isEditMode"
    :isDiff="restriction && restriction.isDiff"
    :isOutdated="isOutdated"
    :itemType="'RESTRICTION'"
    :typename="'flow_restrictions'"
    :item="saveableRestriction"
    :onUpdate="updateRestriction"
    :onCreate="createRestriction"
    :onDelete="deleteRestriction"
    @change-chosen="onChangeChosen"
    @revert="onRevert"
    v-if="$can('access', 'flow_restrictions')"
  >
    <fieldset slot="fieldset" :disabled="isOutdated">
      <affected-modal :ctrl="affectedModalController" :flowsInvoking="affectedFlowsFrom" />
      <h4 class="ui horizontal left aligned divider header">
        {{ $t('RESTRICTION_TARGET_HEADER') }}
      </h4>
      <div class="ui basic segment">
        <div class="ui form" :class="{ error: $v.restriction.$error }">
          <div class="two fields">
            <div
              class="field"
              :class="{ error: $v.restriction.forGroup.$error }"
              v-diff-field="restrictionDiff.forGroup"
            >
              <group-typeahead
                :allGroups="allGroups"
                :currentGroups="currentGroups"
                :multiple="false"
                @selected="selectGroup"
                @removed="removeGroup"
                :disabled="restriction.isDiff || !allGroups"
              />
            </div>
            <div
              class="field"
              :class="{ error: $v.restriction.forVersion.$error }"
              v-diff-field="restrictionDiff.forVersion"
            >
              <dropdown-select
                :labelText="$tc('VERSION', 1)"
                :initialSelection="restriction.forVersion"
                :emitInitialSelection="false"
                :items="availableVersions"
                @selected="selectVersion"
              />
            </div>
          </div>
          <div class="ui error message" v-if="!$v.restriction.uniqueForGroupAndVersion">
            {{ $t('RESTRICTION_GROUP_VERSION_NOT_UNIQUE') }}
          </div>
        </div>
      </div>
      <h4 class="ui horizontal left aligned divider header">
        {{ $t('RESTRICTION_HEADER') }}
      </h4>
      <div class="ui form">
        <div
          class="ui segments"
          v-for="r in $v.restriction.restrictions.$each.$iter"
          :key="r.$model.key"
        >
          <div class="ui segment">
            <div class="two fields">
              <div class="field">
                <dropdown-select
                  :labelText="$tc('MODULE', 1)"
                  :items="modules || []"
                  :initialSelection="r.$model.module"
                  :emitInitialSelection="false"
                  @selected="selectModule($event, r.$model)"
                />
              </div>
              <div class="field" :class="{ error: !r.uniqueInvocation }">
                <dropdown-select
                  :labelText="$tc('METHOD', 1)"
                  :items="r.$model.methods__ || []"
                  :initialSelection="r.$model.method"
                  :emitInitialSelection="false"
                  @selected="selectMethod($event, r.$model)"
                />
                <div class="ui negative message" v-if="!r.uniqueInvocation">
                  {{ $t('RESTRICTION_MUST_HAVE_UNIQUE_INVOCATION') }}
                </div>
              </div>
            </div>
          </div>

          <template v-if="r.$model.expanded__">
            <draggable v-model="r.$model.actions" class="striped" handle=".handle">
              <div class="ui basic segment" v-for="a in r.$model.actions" :key="a.key">
                <div
                  class="handle"
                  style="display: flex; align-items: center; justify-content: center;"
                >
                  <i class="grip lines icon"></i>
                </div>
                <div
                  class="ui top right ribbon label"
                  :class="{
                    red: a.kind === 'DENY' || (a.kind === 'PARAM' && a.action === 'DENY'),
                    blue: a.kind === 'LOG' || (a.kind === 'PARAM' && a.action === 'LOG'),
                    yellow: a.kind === 'PROMPT' || (a.kind === 'PARAM' && a.action === 'PROMPT')
                  }"
                >
                  {{ $t(a.kind + '_LABEL', { action: a.action }) }}
                </div>
                <!-- Restriction options -->
                <dropdown-select
                  :labelText="$t('RESTRICTION_TYPE')"
                  :items="a.restrictionTypes__"
                  :initialSelection="a.kind"
                  :emitInitialSelection="false"
                  @selected="selectRestrictionType($event, a, r.$model.method__)"
                />
                <div class="fields">
                  <template v-for="param in a.kindParams">
                    <div class="field" v-if="param.type === 'String'" :key="param.name">
                      <label :for="param.name">{{ param.description || param.name }}</label>
                      <input
                        :id="param.name"
                        :name="param.name"
                        type="text"
                        v-model="param.value"
                        :placeholder="param.name"
                      />
                    </div>
                    <div class="field" v-else-if="param.type === 'Int'" :key="param.name">
                      <label :for="param.name">{{ param.description || param.name }}</label>
                      <input
                        :id="param.name"
                        :name="param.name"
                        type="number"
                        v-model.number="param.value"
                        :placeholder="param.name"
                      />
                    </div>
                  </template>
                </div>

                <template v-if="a.kind === 'PARAM' && r.$model.method__">
                  <p style="margin-top: 1em;">{{ $t('RESTRICTION_PARAM_BODY') }}</p>
                  <div
                    v-for="(type, name) in r.$model.method__.params"
                    :key="name"
                    v-if="a.paramMatch && a.paramMatch[name]"
                    class="params"
                  >
                    <div v-if="type === 'String'">
                      <regex-field v-model.trim="a.paramMatch[name].match" :labelText="name" />
                    </div>
                    <div class="two fields" v-else-if="type === 'Int32'">
                      <div class="field">
                        <label for="min">{{ $t('MIN', { thing: name }) }}</label>
                        <input
                          id="min"
                          name="min"
                          type="number"
                          v-model.number="a.paramMatch[name].min"
                          :placeholder="$t('MIN')"
                        />
                      </div>
                      <div class="field">
                        <label for="max">{{ $t('MAX', { thing: name }) }}</label>
                        <input
                          id="max"
                          name="max"
                          type="number"
                          v-model.number="a.paramMatch[name].max"
                          :placeholder="$t('MAX')"
                        />
                      </div>
                    </div>
                    <div v-else-if="type === 'Boolean'">
                      <div class="grouped fields">
                        <label>{{ name }}</label>
                        <div class="field">
                          <div class="ui radio checkbox">
                            <input
                              type="radio"
                              :name="r.$model.method + '.' + name"
                              v-model="a.paramMatch[name].is"
                              :value="null"
                            />
                            <label>{{ $t('ANY') }}</label>
                          </div>
                        </div>
                        <div class="field">
                          <div class="ui radio checkbox">
                            <input
                              type="radio"
                              :name="r.$model.method + '.' + name"
                              v-model="a.paramMatch[name].is"
                              :value="true"
                            />
                            <label>{{ $t('TRUE') }}</label>
                          </div>
                        </div>
                        <div class="field">
                          <div class="ui radio checkbox">
                            <input
                              type="radio"
                              :name="r.$model.method + '.' + name"
                              v-model="a.paramsMatch[name].is"
                              :value="false"
                            />
                            <label>{{ $t('FALSE') }}</label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="handle"
                    style="display: flex; align-items: center; justify-content: center;"
                  >
                    <i class="arrow down icon"></i>
                  </div>

                  <p>{{ $t('RESTRICTION_ACTION_BODY') }}</p>
                  <dropdown-select
                    :labelText="$t('RESTRICTION_TYPE')"
                    :items="paramRestrictionActions"
                    :initialSelection="a.action"
                    :emitInitialSelection="false"
                    @selected="selectRestrictionAction($event, a)"
                  />
                  <div class="fields">
                    <template v-for="param in a.actionParams">
                      <div class="field" v-if="param.type === 'String'" :key="param.name">
                        <label :for="param.name">{{ param.description || param.name }}</label>
                        <input
                          :id="param.name"
                          :name="param.name"
                          type="text"
                          v-model="param.value"
                          :placeholder="param.name"
                        />
                      </div>
                      <div class="field" v-else-if="param.type === 'Int'" :key="param.name">
                        <label :for="param.name">{{ param.description || param.name }}</label>
                        <input
                          :id="param.name"
                          :name="param.name"
                          type="number"
                          v-model.number="param.value"
                          :placeholder="param.name"
                        />
                      </div>
                    </template>
                  </div>
                </template>
                <div
                  class="ui mini red labeled icon button"
                  @click="removeAction(r.$model, a)"
                  style="margin-top: 20px;"
                >
                  <i class="delete icon" />
                  {{ $t('DELETE', { thing: $tc('ACTION', 1) }) }}
                </div>
              </div>
            </draggable>
          </template>

          <div class="ui right aligned segment">
            <div class="ui mini basic labeled icon button" @click="toggleExpansion(r.$model)">
              <i class="angle down icon" v-if="r.$model.expanded__" />
              <i class="angle right icon" v-else />
              {{ $t(r.$model.expanded__ ? 'HIDE' : 'SHOW', { thing: $tc('RESTRICTION', 2) }) }}
            </div>
            <div class="ui mini blue labeled icon button" @click="showAffectedFlows(r.$model)">
              <i class="lightbulb icon" />
              {{ $t('AFFECTED', { thing: $tc('FLOW', 2) }) }}
            </div>

            <div class="ui mini red labeled icon button" @click="removeRestriction(r.$model)">
              <i class="delete icon" />
              {{ $t('DELETE', { thing: $tc('RESTRICTION', 1) }) }}
            </div>
            <div class="ui mini green labeled icon button" @click="addAction(r.$model)">
              <i class="add icon" />
              {{ $t('ADD', { thing: $tc('ACTION', 1) }) }}
            </div>
          </div>
        </div>
      </div>
      <div class="ui basic segment">
        <div class="ui mini green right floated labeled icon button" @click="addRestriction">
          <i class="plus icon"></i>
          {{ $t('ADD', { thing: $tc('RESTRICTION', 1) }) }}
        </div>
      </div>
    </fieldset>
  </main-section>
  <div class="ui error message" v-else>
    {{ $t('ACCESS_DENIED', { location: $tc('RESTRICTION', 2) }) }}
  </div>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../scss/variables';

.params {
  margin-bottom: 20px;
}

.menu.transition.visible {
  z-index: 1000 !important;
}

.striped > div:nth-child(odd) {
  background-color: rgba(0, 0, 0, 0.02) !important;
}
</style>

<script>
// @flow
import Vue from 'vue'
import {
  Flow,
  Restrictions,
  CodeCompletions,
  SettingsSuggestions,
  Toast,
  Validation,
  ErrorCodes,
  UnsavedChecker,
  Group
} from 'services'
import { MainSection, RegexField, GroupTypeahead, DropdownSelect, ModalController } from 'ui'
import AffectedModal from './AffectedModal'
import { take } from 'rxjs/operators'
import { required, minLength } from 'vuelidate/lib/validators'
import draggable from 'vuedraggable'

function uniqueForGroupAndVersion(r) {
  return (
    this.mode !== 'create' ||
    _.find(
      this.restrictions,
      other => other && r && other.forGroup === r.forGroup && other.forVersion === r.forVersion
    ) === undefined
  )
}

function uniqueInvocation(r) {
  return !_.find(
    this.restriction.restrictions,
    rst => rst.key !== r.key && rst.method === r.method && rst.module === r.module
  )
}

/* omitDeep function recursively omits properties from an
 * object depending on the predicate given.
 */
function omitDeep(obj, predicate) {
  return _.reduce(
    obj,
    (result, value, key) => {
      if (!predicate(value, key)) {
        if (_.isArray(value)) {
          value = _.map(value, v => omitDeep(v, predicate))
        } else if (_.isObject(value)) {
          value = omitDeep(value, predicate)
        }

        result[key] = value
      }
      return result
    },
    {}
  )
}

export default {
  name: 'restriction',
  mixins: [UnsavedChecker.mixin],
  components: { MainSection, GroupTypeahead, DropdownSelect, RegexField, draggable, AffectedModal },
  props: ['restrictionId'],
  data() {
    return {
      mode: '',
      restriction: { ...Restrictions.defaultRestriction },
      availableVersions: [],
      api: {},
      restrictions: [],
      isOutdated: false,
      restrictionDiff: { restrictions: {} },
      restrictionTypes: [
        { id: 'DENY', name: this.$i18n.t('RESTRICTION_ACTION_KIND_DENY') },
        { id: 'PARAM', name: this.$i18n.t('RESTRICTION_ACTION_KIND_PARAM') },
        {
          id: 'LOG',
          name: this.$i18n.t('RESTRICTION_ACTION_KIND_LOG'),
          params: {
            message: { type: 'String', description: this.$i18n.t('RESTRICTION_LOG_MESSAGE') }
          }
        },
        {
          id: 'PROMPT',
          name: this.$i18n.t('RESTRICTION_ACTION_KIND_PROMPT'),
          params: {
            prompt: { type: 'String', description: this.$i18n.t('RESTRICTION_PROMPT_DESCRIPTION') },
            timeout: { type: 'Int', description: this.$i18n.t('RESTRICTION_TIMEOUT_DESCRIPTION') }
          }
        }
      ],
      affectedFlowsFrom: {},
      affectedModalController: new ModalController({
        header: this.$t('AFFECTED', { thing: this.$tc('FLOW', 2) })
      })
    }
  },

  computed: {
    paramRestrictionActions() {
      return _.filter(this.restrictionTypes, rt => rt.id !== 'PARAM')
    },
    currentGroups() {
      const group = _.find(
        this.$store.state.group.items,
        g => g && g.id === this.restriction.forGroup
      )
      return group ? [group] : []
    },
    allGroups() {
      return this.$store.state.group.items
    },
    allRestrictions() {
      return this.$store.state.flow_restrictions.items
    },
    isEditMode() {
      return this.mode === 'edit'
    },
    modules() {
      return _.uniqBy(
        _.map(_.filter(this.api?.lib, m => m.kind === 'MODULE'), m => ({
          id: m.type,
          name: m.type
        })),
        'id'
      )
    },
    saveableRestriction() {
      return omitDeep(this.restriction, (val, prop) => _.endsWith(prop, '__'))
    }
  },

  validations: {
    restriction: {
      uniqueForGroupAndVersion,
      forGroup: {
        required
      },
      forVersion: {
        required
      },
      restrictions: {
        $each: {
          uniqueInvocation
        }
      }
    }
  },

  methods: {
    showAffectedFlows(r) {
      this.affectedFlowsFrom = { module: r.module, method: r.method }
      this.affectedModalController.show()
    },
    toggleExpansion(r) {
      this.$set(r, 'expanded__', !r.expanded__)
    },
    selectRestrictionAction(action, a) {
      this.$set(a, 'action', action.id)
      if (action.params) {
        this.$set(
          a,
          'actionParams',
          _.map(action.params, (p, name) => ({
            type: p.type,
            name,
            description: p.description,
            value: null
          }))
        )
      } else {
        this.$delete(a, 'actionParams')
      }
    },
    newRestriction() {
      return {
        key: Math.floor(Math.random() * 100000),
        module: '',
        method: '',
        actions: [this.newAction()],

        // internal properties, all props suffixed with "__" are deleted before saving
        method__: null,
        methods__: [],
        restrictionTypes__: [],
        expanded__: true
      }
    },
    newAction(params) {
      return {
        kind: 'DENY',
        key: Math.floor(Math.random() * 100000),
        restrictionTypes__: this.buildRestrictionTypes(params)
      }
    },
    addAction(r) {
      r.actions.push(this.newAction(r.method__?.params))
    },
    removeAction(r, action) {
      r.actions = _.filter(r.actions, a => a.key !== action.key)
    },
    buildParamMatch(params) {
      return _.reduce(
        _.keys(params),
        (memo, name) => {
          if (params[name] === 'String') {
            memo[name] = { match: null }
          } else if (params[name] === 'Int32') {
            memo[name] = { min: 0, max: 999999 }
          } else if (params[name] === 'Boolean') {
            memo[name] = { is: null }
          }
          return memo
        },
        {}
      )
    },
    augmentRestrictionModel() {
      _.each(this.restriction.restrictions, r => {
        // methods__ are the available methods for this module
        this.$set(
          r,
          'methods__',
          _.map(_.find(this.api?.lib, m => m.type === r.module)?.properties, p => ({
            ...p,
            id: p.name,
            name: p.name || p.def
          }))
        )

        // method__ is the currently selected method
        if (r.method) {
          this.$set(r, 'method__', _.find(r.methods__, m => m.name === r.method))
        }

        // each action gets their params rebuilt and the restrictionTypes__ updated
        // to match which types of restrictions are available for the user
        _.each(r.actions, a => {
          if (a.kind === 'PARAM') {
            a.paramMatch = { ...this.buildParamMatch(r.method__?.params), ...a.paramMatch }
          }
          this.$set(a, 'restrictionTypes__', this.buildRestrictionTypes(r.method__?.params))
        })
      })
    },

    selectRestrictionType(rt, a, method__) {
      a.kind = rt.id
      if (a.kind === 'PARAM' && method__) {
        a.paramMatch = this.buildParamMatch(method__.params)
      }
      if (rt.params) {
        this.$set(
          a,
          'kindParams',
          _.map(rt.params, (p, name) => ({
            type: p.type,
            name,
            description: p.description,
            value: null
          }))
        )
      } else {
        this.$delete(a, 'kindParams')
      }
    },
    selectModule(e, t) {
      t.module = e?.name
      this.augmentRestrictionModel()
    },
    selectMethod(e, t) {
      t.method = e?.name

      this.augmentRestrictionModel()
    },
    buildRestrictionTypes(params) {
      // If no params then the PARAM restriction type is not shown
      return _.isEmpty(params)
        ? _.filter(this.restrictionTypes, rt => rt.id !== 'PARAM')
        : this.restrictionTypes
    },
    addRestriction() {
      if (!this.restriction.restrictions) this.$set(this.restriction, 'restrictions', [])
      this.restriction.restrictions.push(this.newRestriction())
    },

    removeRestriction(toRemove) {
      this.restriction.restrictions = _.filter(
        this.restriction.restrictions,
        r => r.key !== toRemove.key
      )
    },
    selectVersion(version) {
      if (!version) return

      this.restriction.forVersion = version.id || version

      // Fetch API for this version
      CodeCompletions.resolveSuggestions(this.restriction.forVersion).then(s => {
        this.api = s.api

        // Pre-fill internal info for dropdowns etc
        this.augmentRestrictionModel()
      })
    },
    selectGroup(group) {
      this.$set(this.restriction, 'forGroup', group.id)
    },

    removeGroup(group) {
      this.restriction.forGroup = null
    },

    readRestriction(id: string) {
      return Restrictions.read(id)
        .then(r => {
          this.restriction = _.cloneDeep(r)
          this.selectVersion(r.forVersion)
          this.isOutdated = false
          this.augmentRestrictionModel()
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('READ', { thing: this.$i18n.tc('RESTRICTION', 1) })
            })
          )
          console.error(err)
        })
    },

    createRestriction() {
      if (!this.$v.$invalid) {
        return Restrictions.create(this.saveableRestriction)
          .then(res => {
            Toast.success(this.$i18n.t('CREATED', { thing: this.$i18n.tc('RESTRICTION', 1) }), {
              remove: 5000
            })
            this.$router.push({
              name: 'restriction',
              params: { restrictionId: res.id }
            })
          })
          .catch(err => {
            Toast.error(
              this.$i18n.t('ERR', {
                action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('RESTRICTION', 1) })
              })
            )
            console.error(err)
          })
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_INCORRECT_INPUT', {
            action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('RESTRICTION', 1) })
          })
        )
        return Promise.reject('Validation failed')
      }
    },

    updateRestriction() {
      if (!this.$v.$invalid) {
        if (!this.isOutdated) {
          return Restrictions.update(this.restriction.id, this.saveableRestriction)
            .then(res => {
              this.restriction.version = res.version
            })
            .catch(err => {
              if (err.code === ErrorCodes.ITEM_OUTDATED) {
                this.isOutdated = true
                Toast.error(
                  this.$i18n.t('ERR_OUTDATED_VERSION', {
                    action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('RESTRICTION', 1) }),
                    thing: this.$i18n.tc('RESTRICTION', 1)
                  })
                )
              } else {
                Toast.error(
                  this.$i18n.t('ERR', {
                    action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('RESTRICTION', 1) })
                  })
                )
                console.error(err)
              }
              throw err
            })
        } else {
          Toast.error(
            this.$i18n.t('ERR_OUTDATED_VERSION', {
              action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('RESTRICTION', 1) }),
              thing: this.$i18n.tc('RESTRICTION', 1)
            })
          )
          return Promise.reject('Outdated version')
        }
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_INCORRECT_INPUT', {
            action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('RESTRICTION', 1) })
          })
        )
        return Promise.reject('Required fields missing')
      }
    },

    deleteRestriction() {
      Restrictions.remove(this.restriction.id)
        .then(() => {
          Toast.success(this.$i18n.t('DELETED', { thing: this.$i18n.tc('RESTRICTION', 1) }), {
            remove: 5000
          })
          this.$router.push({
            name: 'restrictions'
          })
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('DELETE', { thing: this.$i18n.tc('RESTRICTIONS', 1) })
            })
          )
          console.error(err)
        })
    },

    editMode() {
      this.mode = 'edit'
      this.readRestriction(this.restrictionId)

      this.restrictions = []
    },

    createMode() {
      this.mode = 'create'
      this.restriction = { ...Restrictions.defaultRestriction }
      const defaultVersion = _.last(this.availableVersions)
      if (defaultVersion) this.selectVersion(defaultVersion.id)

      this.restrictions = []
      this.addRestriction()
    },

    onChangeChosen({ changedItem, fullDiff }) {
      this.restriction = changedItem
      this.restrictionDiff = !_.isEmpty(fullDiff) ? fullDiff : { restrictions: {} }

      // We need this part bc the list is built first and then saved so it isn't present
      // on the model object before save.

      if (this.restriction.isDiff) this.originalRestrictions = this.restrictions

      this.selectVersion(this.restriction.forVersion)
    },

    onRevert(r) {
      this.restriction = r
      this.restrictionDiff = {}
      this.restrictions = this.originalRestrictions
      this.selectVersion(this.restriction.forVersion)
    }
  },

  mounted() {
    // Populate the versions dropdown
    SettingsSuggestions.getVersions('api').then(versions => {
      // Only one version, set this
      if (versions && versions.length === 1 && !this.restriction.forVersion) {
        this.selectVersion(versions[0])
      }
      this.availableVersions = _.map(versions, v => {
        return { id: v, name: v }
      })
    })

    Group.readAllGroups()

    if (this.restrictionId) {
      this.editMode()
    } else {
      this.createMode()
    }
  },

  watch: {
    restrictionId(newRestrictionId) {
      this.$v.$reset()
      if (newRestrictionId) {
        this.editMode()
      } else {
        this.createMode()
      }
    }
  }
}
</script>
